import { Formik } from "formik";
import { UtilityFormRenderer } from "@modules/stepper-row/components/UtilityFormRenderer";
import React from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType } from "@store/store";
import { Section, YesNoQuestion } from "@modules/stepper-row/types";
import {
  resetSiteRoadwaysROW,
  setSiteRoadwaysROWField,
} from "@modules/stepper-row/redux/siteRoadwaysROWSlice";
import { toast } from "react-toastify";
import { submitSiteRoadwaysROWThunk } from "@modules/stepper-row/redux/thunk/sideRoadwaysThunk";
import {
  sectionsIfOFC,
  sectionsNoOFC,
} from "@modules/stepper-row/components/rowStaticData";
import { useSearchParams } from "react-router-dom";

type LegacySiteRoadwaysROWProps = {
  setSubmitHandler: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  setIsStepValid: (valid: boolean) => void;
};

type FormSyncProps = {
  isFormValid: boolean;
  submitForm: () => Promise<any>;
  setSubmitHandler: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  setIsStepValid: (valid: boolean) => void;
};

const FormSync: React.FC<FormSyncProps> = ({
  isFormValid,
  submitForm,
  setSubmitHandler,
  setIsStepValid,
}) => {
  React.useEffect(() => {
    setIsStepValid(isFormValid);

    setSubmitHandler(() => async () => {
      await submitForm();
    });

    // return () => setSubmitHandler(null);
    return () => {};
  }, [isFormValid, submitForm, setSubmitHandler, setIsStepValid]);

  return null;
};

const LegacySiteRoadwaysROW: React.FC<LegacySiteRoadwaysROWProps> = React.memo(
  ({ setSubmitHandler, setIsStepValid }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();
    const { access_id, amenity, utility_type, category_id } = useSelector(
      (state: RootReducerType) => state.accessLegacy,
    );

    const siteRoadwaysROW = useSelector(
      (state: RootReducerType) => state.siteRoadwaysROW,
    );

    const sections = amenity === "OFC" ? sectionsIfOFC : sectionsNoOFC;
    const urlApplicationId = searchParams.get("row");
    const applicationID = urlApplicationId || access_id;
    const currentApplicationId = applicationID ? String(applicationID) : "";
    const storedApplicationId = String(
      (siteRoadwaysROW as any)?.application_id || "",
    );

    React.useEffect(() => {
      if (!currentApplicationId) {
        dispatch(resetSiteRoadwaysROW());
        return;
      }

      if (storedApplicationId && storedApplicationId !== currentApplicationId) {
        dispatch(resetSiteRoadwaysROW());
      }

      if (storedApplicationId !== currentApplicationId) {
        dispatch(
          setSiteRoadwaysROWField({
            name: "application_id",
            value: currentApplicationId,
          }),
        );
      }
    }, [currentApplicationId, storedApplicationId, dispatch]);

    const getYesNoQuestions = (allSections: Section[]): YesNoQuestion[] => {
      const questions: YesNoQuestion[] = [];

      const traverseFields = (fields: any[]) => {
        for (const field of fields) {
          if (field.type === "radio" && field.id !== undefined) {
            questions.push({
              id: field.id,
              name: field.name,
              label: field.title,
            });
          }
          if (field.nestedFields && Array.isArray(field.nestedFields)) {
            traverseFields(field.nestedFields);
          }
        }
      };

      for (const section of allSections) {
        traverseFields(section.fields);
      }

      return questions;
    };

    const yesNoQuestions = getYesNoQuestions(sections);

    const getVisibleFields = (values: Record<string, any>): string[] => {
      const visibleFields: string[] = [];

      const traverseFields = (fields: any[], parentValue?: string) => {
        for (const field of fields) {
          const isVisible = !parentValue || parentValue === "Yes";

          if (isVisible) {
            if (field.name && field.type !== "radio") {
              visibleFields.push(field.name);
            }

            if (field.type === "radio" && field.name) {
              visibleFields.push(field.name);
            }

            if (field.nestedFields && Array.isArray(field.nestedFields)) {
              const currentFieldValue = field.name
                ? values[field.name]
                : undefined;
              traverseFields(field.nestedFields, currentFieldValue);
            }
          }
        }
      };

      for (const section of sections) {
        traverseFields(section.fields);
      }

      const uniqueFields: string[] = [];
      visibleFields.forEach((field) => {
        if (!uniqueFields.includes(field)) {
          uniqueFields.push(field);
        }
      });

      return uniqueFields;
    };

    const {
      existing_duct_dimension_details,
      duct_utility_details,
      ofc_damage_details,
      right_of_use,
      utilized_nh_land_area,
      proposed_utility_dimension,
      circle_rate,
      available_row_width_left,
      available_row_width_right,
      proposed_utility_distance_left,
      proposed_utility_distance_right,
      land_acquisition_proposal_left,
      land_acquisition_proposal_right,
      total_land_to_be_acquired,
      already_laid_services_desc,
      number_of_existing_lanes,
      left_side_center_line,
      right_side_center_line,
      proposed_service_road_left,
      proposed_service_road_right,
      cable_position_within_right_of_way,
      utility_crossing_arrangement_id,
      cable_encasement_type,
      utility_line_casing_type,
      casing_depth_meters,
      waterway_prevention_compliance,
      laying_methodology,
      trench_refilling_methodology,
      trench_width_over_pipe_cm,
      trench_bedding_depth_cm,
      side_fill_layer_thickness_cm,
      distance_from_duct_edge_within_corridor,
      distance_from_edge_within_corridor,
      questions_true,
      questions_false,
    } = siteRoadwaysROW || {};

    const initialValues: Record<string, any> = {
      ...yesNoQuestions.reduce(
        (acc, question) => {
          if (questions_true?.includes(question.id)) {
            acc[question.name] = "Yes";
          } else if (questions_false?.includes(question.id)) {
            acc[question.name] = "No";
          } else {
            acc[question.name] = "";
          }
          return acc;
        },
        {} as Record<string, any>,
      ),

      existing_duct_dimension_details,
      duct_utility_details,
      ofc_damage_details,
      right_of_use,
      utilized_nh_land_area,
      proposed_utility_dimension,
      circle_rate,
      available_row_width_left,
      available_row_width_right,
      proposed_utility_distance_left,
      proposed_utility_distance_right,
      land_acquisition_proposal_left,
      land_acquisition_proposal_right,
      total_land_to_be_acquired,
      already_laid_services_desc,
      number_of_existing_lanes,
      left_side_center_line,
      right_side_center_line,
      proposed_service_road_left,
      proposed_service_road_right,
      cable_position_within_right_of_way,
      utility_crossing_arrangement_id,
      cable_encasement_type,
      utility_line_casing_type,
      casing_depth_meters,
      waterway_prevention_compliance,
      laying_methodology,
      trench_refilling_methodology,
      trench_width_over_pipe_cm,
      trench_bedding_depth_cm,
      side_fill_layer_thickness_cm,
      distance_from_duct_edge_within_corridor,
      distance_from_edge_within_corridor,
      questions_true,
      questions_false,
    };

    const getAllFieldDefinitions = React.useMemo(() => {
      const allFields: any[] = [];

      const traverseFields = (fields: any[]) => {
        for (const field of fields) {
          allFields.push(field);
          if (field.nestedFields && Array.isArray(field.nestedFields)) {
            traverseFields(field.nestedFields);
          }
        }
      };

      for (const section of sections) {
        traverseFields(section.fields);
      }

      return allFields;
    }, [sections]);

    const validateForm = React.useCallback(
      (values: Record<string, any>) => {
        const errors: Record<string, string> = {};
        const visibleFields = getVisibleFields(values);

        const fieldMap = new Map();
        getAllFieldDefinitions.forEach((field) => {
          if (field.name) {
            fieldMap.set(field.name, field);
          }
        });

        visibleFields.forEach((fieldName) => {
          const value = values[fieldName];
          const fieldDef = fieldMap.get(fieldName);

          if (fieldDef && fieldDef.required !== false) {
            if (fieldDef.type === "radio" && !value) {
              errors[fieldName] = "Required";
            } else if (
              (fieldDef.type === "text" || fieldDef.type === "select") &&
              !value
            ) {
              errors[fieldName] = "Required";
            }
          }

          if (
            fieldDef &&
            fieldDef.dataType === "number" &&
            value &&
            isNaN(Number(value))
          ) {
            errors[fieldName] = "Must be a number";
          }
        });

        return errors;
      },
      [getAllFieldDefinitions],
    );

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validate={validateForm}
        onSubmit={async (values: any) => {
          const nextQuestionsTrue: number[] = [];
          const nextQuestionsFalse: number[] = [];

          yesNoQuestions.forEach(({ id, name }) => {
            const answer = values[name];
            if (answer === "Yes") {
              nextQuestionsTrue.push(id);
            } else if (answer === "No") {
              nextQuestionsFalse.push(id);
            }
          });

          const extractTextAndSelectFields = (
            allSections: Section[],
            payloadValues: any,
          ): Record<string, any> => {
            const payload: Record<string, any> = {};

            const traverseFields = (fields: any[]) => {
              for (const field of fields) {
                if (field.type === "radio" && field.id !== undefined) {
                  if (field.nestedFields && Array.isArray(field.nestedFields)) {
                    traverseFields(field.nestedFields);
                  }
                  continue;
                }

                if (
                  field.type === "radio" &&
                  field.name &&
                  field.id === undefined
                ) {
                  const value = payloadValues[field.name];
                  if (value && value !== "0" && value !== 0) {
                    payload[field.name] = value;
                  }
                }

                if (field.type === "text" || field.type === "select") {
                  const rawValue = payloadValues[field.name];
                  const processedValue =
                    field.dataType === "number" ? Number(rawValue) : rawValue;
                  const isBlank =
                    processedValue === "" ||
                    processedValue === null ||
                    processedValue === undefined ||
                    (typeof processedValue === "number" &&
                      (processedValue === 0 || isNaN(processedValue))) ||
                    processedValue === "0";
                  if (!isBlank) {
                    payload[field.name] = processedValue;
                  }
                }

                if (field.nestedFields && Array.isArray(field.nestedFields)) {
                  traverseFields(field.nestedFields);
                }
              }
            };

            for (const section of allSections) {
              traverseFields(section.fields);
            }

            return payload;
          };

          const inputPayload = extractTextAndSelectFields(sections, values);
          const stringifiedInputPayload = Object.fromEntries(
            Object.entries(inputPayload)
              .filter(([, value]) => {
                const str = String(value);
                return (
                  value !== null &&
                  value !== undefined &&
                  str !== "" &&
                  str !== "0"
                );
              })
              .map(([key, value]) => [key, String(value)]),
          );

          const finalPayload = {
            application_id: applicationID,
            utility_type: utility_type,
            questions_true: nextQuestionsTrue,
            questions_false: nextQuestionsFalse,
            ...stringifiedInputPayload,
          };

          const response = await dispatch(
            submitSiteRoadwaysROWThunk(finalPayload),
          );

          if (response.payload.success) {
            toast.success("ROW Utility Information saved successfully");
          } else {
            toast.error(
              response.payload.message || "Failed to save application",
            );
            throw new Error(
              response.payload.message || "Failed to save application",
            );
          }
        }}
      >
        {({ values, setFieldValue, submitForm }) => {
          const visibleFields = getVisibleFields(values);
          const fieldMap = new Map();

          getAllFieldDefinitions.forEach((field) => {
            if (field.name) {
              fieldMap.set(field.name, field);
            }
          });

          let isFormValid = true;

          for (const fieldName of visibleFields) {
            const fieldDef = fieldMap.get(fieldName);
            const value = values[fieldName];

            if (fieldDef && fieldDef.required !== false) {
              if (fieldDef.type === "radio" && !value) {
                isFormValid = false;
                break;
              } else if (
                (fieldDef.type === "text" || fieldDef.type === "select") &&
                !value
              ) {
                isFormValid = false;
                break;
              }
            }

            if (
              fieldDef &&
              fieldDef.dataType === "number" &&
              value &&
              isNaN(Number(value))
            ) {
              isFormValid = false;
              break;
            }
          }

          return (
            <Box className="amenities-container" component="div">
              <FormSync
                isFormValid={isFormValid}
                submitForm={submitForm}
                setSubmitHandler={setSubmitHandler}
                setIsStepValid={setIsStepValid}
              />

              <Box className="form-wrapper" sx={{ paddingTop: 0 }}>
                <UtilityFormRenderer
                  sections={sections}
                  handleFieldChange={(name, value) => {
                    setFieldValue(name, value);
                    dispatch(setSiteRoadwaysROWField({ name, value }));

                    const updatedValues = { ...values, [name]: value };
                    const updatedQuestionsTrue: number[] = [];
                    const updatedQuestionsFalse: number[] = [];

                    yesNoQuestions.forEach(({ id, name: qName }) => {
                      const answer =
                        qName === name ? value : updatedValues[qName];
                      if (answer === "Yes") {
                        updatedQuestionsTrue.push(id);
                      } else if (answer === "No") {
                        updatedQuestionsFalse.push(id);
                      }
                    });

                    dispatch(
                      setSiteRoadwaysROWField({
                        name: "questions_true",
                        value: updatedQuestionsTrue,
                      }),
                    );
                    dispatch(
                      setSiteRoadwaysROWField({
                        name: "questions_false",
                        value: updatedQuestionsFalse,
                      }),
                    );
                  }}
                />
              </Box>
            </Box>
          );
        }}
      </Formik>
    );
  },
);

export default LegacySiteRoadwaysROW;
